/**
 * The permission keys gating the page on screen right now, registered by
 * `<RequirePermission>` while it is mounted.
 *
 * It exists to answer one question when the server says an account's permissions changed:
 * **is the page the reader is looking at affected?** A page is affected when a key changed in
 * the MODULE its gate belongs to — `roles.read` gates the roles page, so a change to any
 * `roles.*` key changes what that page offers (a button appearing or going) even when the
 * read itself survives.
 *
 * **Known limit:** a page's controls gated on ANOTHER module's key (the roles list's Members
 * column, on `users.read`) are not covered — a change there opens the not-affected notice
 * rather than the affected page. The reader is still told, and still asked to sign in again.
 *
 * Module state rather than React state: it is read inside an event handler and never drives a
 * render, and it is written from effects only, never during render.
 */
const gates = new Map<symbol, readonly string[]>();

/** Registers a page's gate; call the returned function on unmount. */
export function registerPageGate(keys: readonly string[]): () => void {
  const id = Symbol('page-gate');
  gates.set(id, keys);
  return () => {
    gates.delete(id);
  };
}

/** The module prefix of a key — `roles` for `roles.assign-permissions`. */
export function moduleOfKey(key: string): string {
  const dot = key.indexOf('.');
  return dot === -1 ? key : key.slice(0, dot);
}

/** True when any changed key belongs to a module one of the current page's gates names. */
export function currentPageAffectedBy(changedKeys: readonly string[]): boolean {
  const modules = new Set([...gates.values()].flat().map(moduleOfKey));
  return changedKeys.some((key) => modules.has(moduleOfKey(key)));
}
